import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../../../components/Common/Toast';
import config from '../../../config';
import {
  FaTimes, FaUserCheck, FaUserTimes, FaUserClock,
  FaChartBar, FaCheck, FaClock
} from 'react-icons/fa';
import './MilitaryEndReviewModal.css';

const MilitaryEndReviewModal = ({
  show,
  onClose,
  batchId,
  teamId,
  token,
  onCandidateAction
}) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [assessmentInfo, setAssessmentInfo] = useState(null);

  useEffect(() => {
    if (show && batchId) {
      fetchCandidates();
    }
  }, [show, batchId]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${config.baseURL}/api/military/batch/${batchId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        setAssessmentInfo({
          team_name: data.team_name,
          assessment_mode: data.assessment_mode,
          current_week: data.current_week,
          total_weeks: data.total_weeks
        });

        // ✅ Map candidates with their current status from the backend
        setCandidates(data.candidates.map(c => ({
          soldier_id: c.soldier_id || c.soldierId || c.id,
          participant_id: c.participant_id,
          candidate_name: c.candidate_name,
          candidate_email: c.candidate_email,
          program_id: c.program_id,
          session_id: c.session_id || c.individual_session_id,
          session_status: c.session_status,
          answered_count: c.answered_count,
          total_questions: c.total_questions,
          progress: c.progress,
          status: c.status || null,
          has_analytics: c.has_analytics,
          analytics_summary: c.analytics_summary,
          current_week: c.current_week,
          week_number: c.week_number
        })));
      } else {
        toast.error(data.message || 'Failed to fetch candidates');
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (soldierId, action) => {
    if (actionLoading[soldierId]) return;

    setActionLoading(prev => ({ ...prev, [soldierId]: true }));

    try {
      const { data } = await axios.post(
        `${config.baseURL}/api/military/soldiers/${soldierId}/status`,
        {
          status: action,
          batch_id: batchId,
          team_id: teamId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success(`Soldier ${action === 'selected' ? 'accepted' : action === 'rejected' ? 'rejected' : 'marked as pending'}`);
        setCandidates(prev => prev.map(c =>
          c.soldier_id === soldierId ? { ...c, status: action } : c
        ));

        // ✅ Notify parent if callback provided
        if (onCandidateAction) {
          onCandidateAction(soldierId, action);
        }
      }
    } catch (error) {
      console.error('Error updating soldier:', error);
      toast.error('Failed to update status');
    } finally {
      setActionLoading(prev => ({ ...prev, [soldierId]: false }));
    }
  };

  const handleViewAnalytics = (candidate) => {
    console.log("FULL candidate object:", candidate);
    console.log("candidate keys:", Object.keys(candidate));

    const soldierId =
      candidate.soldier_id ||
      candidate.soldierId ||
      candidate.id ||
      candidate.user_id ||
      candidate.userId ||
      candidate.candidate_id ||
      candidate.candidateId;

    const sessionId =
      candidate.session_id ||
      candidate.sessionId ||
      candidate.individual_session_id ||
      candidate.individualSessionId;

    console.log("Resolved analytics IDs:", {
      sessionId,
      soldierId,
      candidate
    });

    if (!sessionId || !soldierId) {
      toast.error("Session ID or Soldier ID missing. Check console.");
      return;
    }

    window.dispatchEvent(new CustomEvent("openAnalyticsModal", {
      detail: {
        interviewId: sessionId,
        soldierId,
        candidate,
        batchId,
        candidateName: candidate.candidate_name,
        isMilitary: true
      }
    }));
  };
  const reviewedCount = candidates.filter(c =>
    c.status === 'selected' || c.status === 'rejected'
  ).length;

  const pendingCount = candidates.filter(c =>
    c.status === 'pending' || c.status === null
  ).length;

  const allDecided = candidates.length > 0 && reviewedCount === candidates.length;

  if (!show) return null;

  return (
    <div className="end-interview-overlay" onClick={onClose}>
      <div className="end-interview-modal" onClick={e => e.stopPropagation()}>
        <div className="end-interview-header">
          <div>
            <h2>Review Soldiers</h2>
            <p className="subtitle">
              Manually accept, reject, or mark soldiers as pending
              {assessmentInfo && (
                <span className="assessment-info">
                  {' '}• Week {assessmentInfo.current_week} of {assessmentInfo.total_weeks}
                </span>
              )}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading soldiers...</p>
          </div>
        ) : candidates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No Soldiers Found</h3>
            <p>No soldiers have joined this assessment yet</p>
          </div>
        ) : (
          <>
            <div className="review-progress">
              <div className="progress-header">
                <span>Review Progress</span>
                <span className="progress-count">
                  {reviewedCount} reviewed, {pendingCount} unreviewed
                </span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${candidates.length > 0 ? (reviewedCount / candidates.length) * 100 : 0}%` }}
                />
              </div>
            </div>

            <div className="candidates-list">
              {candidates.map(candidate => (
                <div
                  key={candidate.soldier_id || candidate.participant_id}
                  className={`candidate-card ${candidate.status || 'unreviewed'}`}
                >
                  <div className="candidate-info">
                    <div className="candidate-name-section">
                      <h4>{candidate.candidate_name}</h4>
                      {candidate.status === 'selected' && (
                        <span className="status-badge-mini selected">✓ Selected</span>
                      )}
                      {candidate.status === 'rejected' && (
                        <span className="status-badge-mini rejected">✗ Rejected</span>
                      )}
                      {candidate.status === 'pending' && (
                        <span className="status-badge-mini pending">⏳ Pending</span>
                      )}
                      {!candidate.status && (
                        <span className="status-badge-mini unreviewed">⬜ Unreviewed</span>
                      )}
                    </div>
                    <span className="candidate-email">{candidate.candidate_email}</span>

                    <div className="answer-progress">
                      <div className="progress-label">
                        <span>Questions answered</span>
                        <span className="progress-count">
                          {candidate.answered_count}/{candidate.total_questions}
                        </span>
                      </div>
                      <div className="mini-progress">
                        <div
                          className="mini-progress-fill"
                          style={{
                            width: `${candidate.progress || 0}%`
                          }}
                        />
                      </div>
                    </div>

                    <div className="completion-badge">
                      {candidate.progress || 0}% complete
                    </div>

                    {/* ✅ Show analytics status */}
                    {candidate.has_analytics && (
                      <div className="analytics-badge">
                        📊 Analytics available
                      </div>
                    )}
                  </div>

                  <div className="candidate-actions">
                    <button
                      className={`action-btn select-btn ${candidate.status === 'selected' ? 'active' : ''}`}
                      onClick={() => handleAction(candidate.soldier_id, 'selected')}
                      disabled={actionLoading[candidate.soldier_id]}
                      title="Select Soldier"
                    >
                      <FaUserCheck />
                      <span>
                        {actionLoading[candidate.soldier_id] && candidate.status !== 'selected'
                          ? 'Selecting...'
                          : candidate.status === 'selected' ? 'Selected' : 'Select'}
                      </span>
                    </button>

                    <button
                      className={`action-btn reject-btn ${candidate.status === 'rejected' ? 'active' : ''}`}
                      onClick={() => handleAction(candidate.soldier_id, 'rejected')}
                      disabled={actionLoading[candidate.soldier_id]}
                      title="Reject Soldier"
                    >
                      <FaUserTimes />
                      <span>
                        {actionLoading[candidate.soldier_id] && candidate.status !== 'rejected'
                          ? 'Rejecting...'
                          : candidate.status === 'rejected' ? 'Rejected' : 'Reject'}
                      </span>
                    </button>

                    <button
                      className={`action-btn pending-btn ${candidate.status === 'pending' ? 'active' : ''}`}
                      onClick={() => handleAction(candidate.soldier_id, 'pending')}
                      disabled={actionLoading[candidate.soldier_id]}
                      title="Mark as Pending"
                    >
                      <FaUserClock />
                      <span>
                        {actionLoading[candidate.soldier_id] && candidate.status !== 'pending'
                          ? 'Updating...'
                          : candidate.status === 'pending' ? 'Pending' : 'Pending'}
                      </span>
                    </button>

                    <button
                      className="action-btn analytics-btn"
                      onClick={() => handleViewAnalytics(candidate)}
                      title="View Detailed Analytics"
                      disabled={!candidate.has_analytics}
                    >
                      <FaChartBar />
                      <span>{candidate.has_analytics ? 'Details' : 'No Data'}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="modal-footer">
          <div className="footer-info">
            {allDecided ? (
              <span className="all-reviewed-message">
                ✅ All soldiers reviewed - you can close this window
              </span>
            ) : pendingCount > 0 ? (
              <span className="pending-message">
                ⚠️ {pendingCount} soldier{pendingCount !== 1 ? 's' : ''} still unreviewed
              </span>
            ) : (
              <span className="pending-message">
                ⚠️ Please review all soldiers
              </span>
            )}
          </div>
          <div className="footer-actions">
            <button className="eim-cancel-btn text-light" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MilitaryEndReviewModal;